import React, { useState, useEffect, useCallback } from 'react';
import PageContainer from '../components/Layout/PageContainer';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Badge from '../components/UI/Badge';
import DataTable from '../components/UI/DataTable';
import { Filter, RefreshCw, CheckCircle, ExternalLink } from 'lucide-react';
import { apiClient } from '../api';

const SEVERITY_CONFIG = {
  high: { label: 'HIGH', variant: 'danger' },
  medium: { label: 'MEDIUM', variant: 'warning' },
  low: { label: 'LOW', variant: 'success' },
  undefined: { label: 'UNDEFINED', variant: 'default' },
};

const IOC_TYPE_LABELS = {
  ip: 'IP',
  domain: 'Domain',
  hash: 'Hash',
  filename: 'Filename',
  url: 'URL',
};

const Events = () => {
  const [alerts, setAlerts] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  const [resolvedFilter, setResolvedFilter] = useState('0');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [serverFilter, setServerFilter] = useState('all');

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (resolvedFilter !== 'all') {
        params.set('resolved', resolvedFilter);
      }
      if (serverFilter !== 'all') {
        params.set('server_id', serverFilter);
      }

      const res = await apiClient.get(`/api/alerts/ioc?${params.toString()}`);
      setAlerts(res.data.ioc_alerts || []);
    } catch (err) {
      console.error('Failed to fetch IoC alerts:', err);
      setError(err.response?.data?.error || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [resolvedFilter, serverFilter]);

  const fetchServers = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/servers');
      setServers(res.data.servers || []);
    } catch (err) {
      console.error('Failed to fetch servers:', err);
    }
  }, []);

  useEffect(() => {
    fetchServers();
  }, [fetchServers]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleResolve = async (id) => {
    setResolvingId(id);
    try {
      await apiClient.patch(`/api/alerts/ioc/${id}`);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Failed to resolve alert:', err);
      setError('Failed to resolve alert');
    } finally {
      setResolvingId(null);
    }
  };

  const getServerName = (serverId) => {
    const server = servers.find((s) => s.id === serverId);
    return server ? server.hostname : serverId;
  };

  const filteredAlerts = severityFilter === 'all'
    ? alerts
    : alerts.filter((a) => a.misp_threat_level === severityFilter);

  const columns = [
    {
      header: 'Severity',
      width: '12%',
      render: (row) => {
        const config = SEVERITY_CONFIG[row.misp_threat_level] || SEVERITY_CONFIG.undefined;
        return <Badge variant={config.variant}>{config.label}</Badge>;
      },
    },
    {
      header: 'IoC Type',
      width: '10%',
      render: (row) => (
        <span className="font-medium capitalize">{IOC_TYPE_LABELS[row.ioc_type] || row.ioc_type}</span>
      ),
    },
    {
      header: 'IoC Value',
      width: '20%',
      render: (row) => (
        <code className="text-xs bg-slate-100 px-2 py-1 rounded" title={row.ioc_value}>
          {row.ioc_value.length > 30 ? row.ioc_value.substring(0, 30) + '...' : row.ioc_value}
        </code>
      ),
    },
    {
      header: 'MITRE ATT&CK',
      width: '12%',
      render: (row) =>
        row.attack_technique ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-indigo-100 text-indigo-700 border border-indigo-200">
            {row.attack_technique}
          </span>
        ) : (
          <span className="text-slate-400 text-xs">—</span>
        ),
    },
    {
      header: 'Server',
      width: '15%',
      render: (row) => <span className="text-sm">{getServerName(row.server_id)}</span>,
    },
    {
      header: 'Description',
      width: '15%',
      render: (row) => (
        <span className="text-sm text-slate-500 truncate block max-w-[200px]" title={row.description}>
          {row.description || '—'}
        </span>
      ),
    },
    {
      header: 'Detected',
      width: '10%',
      render: (row) => (
        <span className="text-xs text-slate-500">
          {row.detected_at ? new Date(row.detected_at).toLocaleDateString() : 'N/A'}
        </span>
      ),
    },
    {
      header: 'Actions',
      width: '6%',
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.misp_event_id && (
            <a
              href={`#/misp/event/${row.misp_event_id}`}
              title="View in MISP"
              className="text-slate-400 hover:text-blue-600 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {!row.resolved && (
            <button
              onClick={() => handleResolve(row.id)}
              disabled={resolvingId === row.id}
              className="text-slate-400 hover:text-green-600 transition-colors disabled:opacity-50"
              title="Resolve alert"
            >
              {resolvingId === row.id ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
            </button>
          )}
          {row.resolved && (
            <Badge variant="success">Resolved</Badge>
          )}
        </div>
      ),
    },
  ];

  const actions = (
    <Button variant="outline" size="sm" onClick={fetchAlerts}>
      <RefreshCw className="w-4 h-4 mr-2" />
      Refresh
    </Button>
  );

  return (
    <PageContainer
      title="IoC Alerts"
      subtitle="Indicators of Compromise detected from MISP enrichment"
      actions={actions}
    >
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Filter className="w-4 h-4 text-slate-400" />
        <span className="text-sm text-slate-500">Filters:</span>

        <select
          value={resolvedFilter}
          onChange={(e) => setResolvedFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-950 text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <option value="0">Unresolved</option>
          <option value="1">Resolved</option>
          <option value="all">All</option>
        </select>

        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-950 text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <option value="all">All Severities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="undefined">Undefined</option>
        </select>

        <select
          value={serverFilter}
          onChange={(e) => setServerFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-950 text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <option value="all">All Servers</option>
          {servers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.hostname}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <Card className="mb-6">
          <div className="p-4 text-red-600 bg-red-50 rounded-lg">
            <strong>Error:</strong> {error}
            <button
              onClick={fetchAlerts}
              className="ml-4 text-sm underline hover:text-red-800"
            >
              Retry
            </button>
          </div>
        </Card>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={filteredAlerts}
          emptyMessage={
            loading
              ? 'Loading IoC alerts...'
              : filteredAlerts.length === 0 && alerts.length > 0
                ? 'No alerts match the current filters.'
                : 'No IoC alerts found. Alerts are generated when agents send heartbeats or snapshots.'
          }
        />
      </Card>
    </PageContainer>
  );
};

export default Events;
